/* eslint-disable tailwindcss/no-arbitrary-value */
import classNames from 'classnames';
import { ApplyCtaLink } from '../../../components/ApplyCta';
import Button from '../../../components/Button';
import { FeLink } from '../../../components/FeLink';
import {
  ComponentTypeEnum,
  sendProductAnalytics,
  UserActionEnum,
} from '../../../utils/analytics/sendAnalytics';
import { IDENTITY_CELL_PADDING } from '../constants';
import { IdentityCellProps } from '../types';

export const IdentityCell = ({
  row,
  applyLabel,
  isScrolled,
  pushDataLayer,
  index,
  titleTableBuilder,
  loanSearchParams,
  defaultTripCriteria,
}: IdentityCellProps) => {
  const {
    identityCellDisplayVariant,
    identityCellProductRef,
    identityCellNameOverride,
    identityCellCtaOverrideLabel,
    identityCellCtaOverrideUrl,
    identityCellCtaOverrideDestinationType,
    identityCellCtaOverrideTrackingCode,
  } = row;

  const name =
    identityCellNameOverride || identityCellProductRef?.product_name || '';
  const showLogo =
    (identityCellDisplayVariant === 'LOGO_NAME_CTA' ||
      identityCellDisplayVariant === 'LOGO_NAME') &&
    !!identityCellProductRef?.product_image;
  const showCta =
    identityCellDisplayVariant === 'LOGO_NAME_CTA' ||
    identityCellDisplayVariant === 'NAME_CTA';

  // CTA source: manual (authored URL, no product) vs catalogue (product-driven).
  const isManualCta = !identityCellProductRef;

  // Manual CTA: all authored fields must be present.
  const isManualCtaValid =
    isManualCta &&
    !!identityCellCtaOverrideLabel &&
    !!identityCellCtaOverrideUrl &&
    !!identityCellCtaOverrideTrackingCode &&
    !!identityCellCtaOverrideDestinationType;

  // Catalogue CTA: waterfall label resolution (override → product CTA → default).
  const ctaLabel = isManualCta
    ? identityCellCtaOverrideLabel
    : identityCellCtaOverrideLabel ||
      identityCellProductRef?.call_to_action?.links?.[0]?.label ||
      applyLabel;

  // Manual EXTERNAL destination opens in a new tab (per spec).
  const isExternalManualCta =
    isManualCta && identityCellCtaOverrideDestinationType === 'EXTERNAL';

  const handleCtaClick = () => {
    if (!pushDataLayer) return;

    const componentTitle = `${titleTableBuilder}/CTA_${ctaLabel}`;
    const itemListName = titleTableBuilder?.toLowerCase()?.replace(/\s+/g, '-');

    if (isManualCta) {
      pushDataLayer({
        event: 'navigate',
        action: 'click',
        component_type: 'table',
        component_title: componentTitle,
        tracking_code: identityCellCtaOverrideTrackingCode ?? '',
        destination_type: identityCellCtaOverrideDestinationType ?? '',
        itemListName: itemListName,
      });
    } else if (identityCellProductRef) {
      sendProductAnalytics({
        action: UserActionEnum.CLICK,
        componentTitle: componentTitle,
        componentType: ComponentTypeEnum.TABLE,
        event: 'product_click_apply',
        index,
        product: identityCellProductRef,
        pushDataLayer,
        itemListName: itemListName,
      });
    }
  };

  // `.mhg-alpha-btn` sets `text-nowrap` in `@layer components`, and the identity
  // track is a hard 120px at 360; `ctaLabel` is authored, so an override longer
  // than "Apply Now" is ordinary content that has to wrap rather than be clipped.
  const ctaButton = (
    <Button
      size="small"
      styleType="cta"
      variant="outline"
      className="whitespace-normal"
    >
      {ctaLabel}
    </Button>
  );

  return (
    <td
      // The identity cell labels its row (product name + CTA), so it is the
      // row header rather than a data cell. Under the grid + explicit-ARIA
      // approach that distinction has to be stated, or assistive tech loses
      // the row's context when moving across its values. Matches
      // `SimilarProducts`.
      //
      // The rule's allow-list for `<td>` is `gridcell` only, so it reads
      // `rowheader` as an interactive role on a non-interactive element.
      // `rowheader` is a valid cell role per ARIA, and it is load-bearing here
      // for the reason above — same justification as the rules disabled at the
      // top of this file.
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
      role="rowheader"
      className={classNames(
        'sticky left-0 z-20',
        'bg-fill/base group-hover/row:bg-fill/base',
        'border-t border-borders/default',
        'min-h-table/cell-min-height',
        IDENTITY_CELL_PADDING,
        // The cell is `sticky` against unpositioned value cells, so anything the
        // fixed track cannot fit — a single unbreakable word — would otherwise
        // paint over the first data column as the table scrolls.
        'break-words overflow-hidden',
        { 'shadow-[4px_0px_6px_-1px_rgba(0,0,0,0.10)]': isScrolled },
      )}
    >
      <div className="flex h-full flex-col items-start justify-center gap-4">
        {showLogo && (
          // eslint-disable-next-line tailwindcss/no-arbitrary-value, tailwindcss/no-custom-classname
          <div className="inline-flex h-8 max-h-10 w-14 max-w-16 items-center justify-center rounded-[5px] border border-borders/default p-[3px]">
            <img
              src={identityCellProductRef!.product_image!}
              alt={name}
              // eslint-disable-next-line tailwindcss/no-arbitrary-value
              className="size-full rounded-[1px] object-contain"
            />
          </div>
        )}
        <span
          className={classNames(
            'font-font-weight/table-cell-strong text-font-size/table-cell leading-line-height/table-cell',
            'text-text/primary',
          )}
        >
          {name}
        </span>
        {showCta &&
          (isManualCta
            ? isManualCtaValid && (
                <FeLink
                  href={identityCellCtaOverrideUrl!}
                  className="no-underline pb-1.5 md:pb-0"
                  target={isExternalManualCta ? '_blank' : undefined}
                  rel={isExternalManualCta ? 'noopener noreferrer' : undefined}
                  onClick={handleCtaClick}
                >
                  {ctaButton}
                </FeLink>
              )
            : // Catalogue CTA. `ApplyCtaLink` resolves the URL from context
              // (vertical / pageType / travelQuotesData / brand / langPrefix / GE
              // config) via `useCtaUrl`, applies the apply-v3 modal
              // intercept + UTM/AdBlock handling, and self-hides when nothing
              // resolves — so no explicit visibility gate is needed here.
              // `e2eCTA` stays false until MetaConfig wires the product toggle
              // (ITSM-80493 follow-up), so trip criteria are not appended yet.
              identityCellProductRef && (
                <ApplyCtaLink
                  product={identityCellProductRef}
                  ctaLinkOverride={identityCellCtaOverrideUrl}
                  loanParams={loanSearchParams}
                  e2eCTA={false}
                  // CMS-1851: Strapi default setting → replacement criteria
                  // when the RP filter trip type conflicts with the product's.
                  travelDefaults={defaultTripCriteria}
                  className="no-underline pb-1.5 md:pb-0"
                  onClick={handleCtaClick}
                >
                  {ctaButton}
                </ApplyCtaLink>
              ))}
      </div>
    </td>
  );
};
